import {
  FormikErrorMessage,
  Modal,
  Row,
  Section,
  Button,
  TextInput
} from 'shared'
import { Formik, Form, FormikHelpers, FormikProps } from 'formik'
import { Panel, Col, ButtonToolbar, SelectPicker } from 'rsuite'
import { useMemo } from 'react'
import { notifyError, notifySuccess } from 'utils'
import {
  defaultRuleFormValues,
  IRuleForm,
  RULE_FORM_FIELDS,
  ruleValidationSchema,
  getInitialRuleFormValueFromResponse
} from '../utils'
import './createRule.scss'
import { useCreateRuleMutation, useUpdateRuleMutation } from '../RuleApiSlice'
import { Rule } from '../types'

interface CreateRuleProps {
  open: boolean;
  onClose: () => void;
  isEditMode: boolean;
  rule?: any;
}
const CreateRule: React.FC<CreateRuleProps> = ({
  open,
  onClose,
  isEditMode,
  rule
}) => {
  const Ruleid = rule?.id
  const initialValues = useMemo(() => {
    const RuleData =
      Ruleid && rule
        ? getInitialRuleFormValueFromResponse(rule)
        : defaultRuleFormValues
    return RuleData
  }, [Ruleid, rule])
  const [createRule] = useCreateRuleMutation()
  const [updateRule] = useUpdateRuleMutation()
  const renderFormButtons = (formikProps: FormikProps<IRuleForm>) => (
    <ButtonToolbar>
      {isEditMode ? (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save changes
          </Button>
        </>
      ) : (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save
          </Button>
        </>
      )}
    </ButtonToolbar>
  )

  const DataTypeOptions = [
    { label: 'String', value: 'String' },
    {
      label: 'Number',
      value: 'Number'
    },
    { label: 'Float', value: 'Float' }
  ]

  const onSubmit = async (
    formValues: IRuleForm,
    { setSubmitting }: FormikHelpers<IRuleForm>
  ) => {
    const ruleDTO: Rule = {
      ruleName: formValues.ruleName,
      fieldPath: formValues.fieldPath,
      salesforceFieldPath: formValues.salesforceFieldPath,
      dataType: formValues.dataType
    }

    try {
      if (isEditMode) {
        await updateRule({
          ruleDTO,
          id: Ruleid
        }).unwrap()

        notifySuccess('Rule Update successfully!')
        onClose()
      } else {
        await createRule({ ruleDTO }).unwrap()
        notifySuccess('Rule created successfully!')
        onClose()
      }
    } catch (error) {
      if (isEditMode) {
        notifyError('Failed to update Rule.', {
          containerId: 'modalToast'
        })
      } else {
        notifyError('Failed to create Rule.', {
          containerId: 'modalToast'
        })
      }
    }
  }

  return (
    <Modal
      secondary
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Rule' : 'Add Rule'}
      size="calc(50% - 120px)"
      body={
        <Formik
          initialValues={initialValues}
          validationSchema={ruleValidationSchema}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {(formikProps: FormikProps<IRuleForm>) => (
            <Form className="create-rule">
              <Panel>
                <Section title="Rule Details">
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={RULE_FORM_FIELDS.NAME}
                        placeholder="Enter rule name"
                        dataType="string"
                      />
                      <FormikErrorMessage name={RULE_FORM_FIELDS.NAME} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={RULE_FORM_FIELDS.FIELDPATH}
                        placeholder="Enter field path"
                        dataType="string"
                      />
                      <FormikErrorMessage name={RULE_FORM_FIELDS.FIELDPATH} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={RULE_FORM_FIELDS.SALESFORCEFIELDPATH}
                        placeholder="Enter Salesforce Path"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={RULE_FORM_FIELDS.SALESFORCEFIELDPATH}
                      />
                    </Col>
                    <Col xs={12}>
                      <SelectPicker
                        data={DataTypeOptions}
                        placeholder="Select Data Type"
                        value={formikProps.values[RULE_FORM_FIELDS.DATA_TYPE]}
                        onChange={(value) =>
                          formikProps.setFieldValue(
                            RULE_FORM_FIELDS.DATA_TYPE,
                            value
                          )
                        }
                        cleanable
                        style={{ width: '100%' }}
                      />
                      <FormikErrorMessage name={RULE_FORM_FIELDS.DATA_TYPE} />
                    </Col>
                  </Row>
                </Section>
              </Panel>
              <Panel className="panel-buttons">
                {renderFormButtons(formikProps)}
              </Panel>
            </Form>
          )}
        </Formik>
      }
    />
  )
}
export default CreateRule
